import * as React from 'react';
import { Popover, Space, type Button } from 'antd';
import { PropagationButton } from '@shared/components/PropagationDisplay';
import PropagationRadioGroup from '@shared/components/PropagationRadioGroup';

export interface PopoverButtonProps {
  value?: Propagation;
  stepOptions?: GetProp<typeof PropagationRadioGroup, 'stepOptions'>;
  popoverProps?: GetProps<typeof Popover>;
  buttonProps?: GetProps<typeof Button>;
  children?: Propagation;
  onChange?: (value: Propagation) => void;
}

const handleStopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const PopoverButton = (props: PopoverButtonProps) => {
  const { value, stepOptions, children, onChange, popoverProps = {}, buttonProps = {} } = props;
  const { onOpenChange: onPopoverOpenChange } = popoverProps;
  const { onClick: onButtonClick } = buttonProps;

  const [open, setOpen] = React.useState<boolean | undefined>(undefined);

  const handleChange = React.useCallback<GetProp<typeof PropagationRadioGroup, 'onChange'>>(
    (value) => {
      setOpen(false);
      onPopoverOpenChange?.(false);

      return onChange?.(value);
    },
    [onChange, onPopoverOpenChange],
  );
  const handlePopoverOpenChange = React.useCallback<GetProp<typeof Popover, 'onOpenChange'>>(
    (open, e) => {
      setOpen(open);

      return onPopoverOpenChange?.(open, e);
    },
    [onPopoverOpenChange],
  );
  const handleButtonClick = React.useCallback<GetProp<typeof Button, 'onClick'>>(
    (e) => {
      e.stopPropagation();

      return onButtonClick?.(e);
    },
    [onButtonClick],
  );

  React.useEffect(() => {
    if (!open) {
      setOpen(undefined);
    }
  }, [open]);

  return (
    <Popover
      {...popoverProps}
      open={open}
      trigger={['click']}
      placement="right"
      destroyOnHidden
      content={
        <Space
          style={{
            padding: 12,
          }}
          onMouseDown={handleStopPropagation}
          onClick={handleStopPropagation}
        >
          Propagation Method
          <PropagationRadioGroup value={value} stepOptions={stepOptions} onChange={handleChange} />
        </Space>
      }
      styles={{
        body: { padding: 0 },
      }}
      onOpenChange={handlePopoverOpenChange}
    >
      <PropagationButton
        {...buttonProps}
        onMouseDown={handleStopPropagation}
        onClick={handleButtonClick}
      >
        {children}
      </PropagationButton>
    </Popover>
  );
};

export const MetricsDimensionsPopoverButton = React.memo(PopoverButton);
